import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { allComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您要查看的漫画不存在或已被移除</p>
            <Link to="/" className="inline-flex px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formattedViews = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();
  const formattedFavorites = comic.favorites >= 10000 ? `${(comic.favorites / 10000).toFixed(1)}万` : comic.favorites.toString();

  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类', path: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleSchema = {
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    image: comic.cover,
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebSite" />
      <SEOSchema type="Article" data={articleSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            <article className="mt-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-72 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      title={`${comic.title} - 虫虫动漫免费漫画`}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mt-2 mb-4">
                    <span className="text-sm text-foreground-500">
                      <strong className="text-foreground-700">作者：</strong>{comic.author}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-sm text-foreground-500">
                      <strong className="text-foreground-700">更新：</strong>{comic.updateDate}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400"></i>
                      <span className="text-lg font-bold text-accent-600">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">({comic.ratingCount}人评分)</span>
                    </div>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-eye-line mr-1"></i>{formattedViews} 阅读
                    </span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-heart-line mr-1"></i>{formattedFavorites} 收藏
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-background-100 text-foreground-600 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-lg p-4 mb-4">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      开始阅读第一话
                    </button>
                    <button className="px-6 py-2.5 border border-background-200 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                      从最新话开始
                    </button>
                  </div>
                </div>
              </div>

              <section className="mt-10" aria-label="章节列表">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">
                  章节列表
                  <span className="text-sm font-normal text-foreground-400 ml-2">共{comic.chapters.length}话</span>
                </h2>
                <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <div className="max-h-[500px] overflow-y-auto">
                    {comic.chapters.map((chapter, index) => (
                      <Link
                        key={chapter.id}
                        to={`/article/${comic.id}/${comic.slug}?chapter=${chapter.number}`}
                        className={`flex items-center justify-between px-4 py-3 text-sm transition-colors hover:bg-background-100 ${
                          index < comic.chapters.length - 1 ? 'border-b border-background-200/50' : ''
                        }`}
                      >
                        <span className="text-foreground-700 truncate flex-1">{chapter.title}</span>
                        <span className="text-xs text-foreground-400 ml-4 whitespace-nowrap">{chapter.updateDate}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>

              {comic.reviews.length > 0 && (
                <section className="mt-10" aria-label="读者评价">
                  <h2 className="text-lg font-heading text-foreground-900 mb-4">
                    读者评价
                    <span className="text-sm font-normal text-foreground-400 ml-2">({comic.reviews.length}条)</span>
                  </h2>
                  <div className="space-y-4">
                    {comic.reviews.map((review) => (
                      <div key={review.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs font-bold">
                              {review.username.charAt(0)}
                            </div>
                            <span className="text-sm font-medium text-foreground-800">{review.username}</span>
                          </div>
                          <span className="text-xs text-foreground-400">{review.date}</span>
                        </div>
                        <div className="flex items-center gap-0.5 mb-2">
                          {Array.from({ length: 5 }, (_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button className="text-xs text-foreground-400 hover:text-accent-500 transition-colors cursor-pointer flex items-center gap-1">
                            <i className="ri-thumb-up-line"></i> {review.likes}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {relatedComics.length > 0 && (
                <section className="mt-10" aria-label="相关推荐">
                  <h2 className="text-lg font-heading text-foreground-900 mb-4">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/article/${rc.id}/${rc.slug}`}
                        className="group"
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70 mb-2">
                          <img
                            src={rc.cover}
                            alt={rc.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            title={`${rc.title} - 虫虫动漫免费漫画`}
                          />
                        </div>
                        <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                          {rc.title}
                        </h3>
                        <p className="text-xs text-foreground-400">{rc.author}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}